<template>
    <v-container class="py-6" style="max-width: 600px;">
        <v-card>
            <v-card-title class="text-h6">
                <v-icon icon="mdi-clipboard-text" class="mr-2" />
                Encuesta
            </v-card-title>
            <v-card-text>
                <v-form ref="formRef" @submit.prevent="submitForm">
                    <v-select
                        v-model="form.gender"
                        :items="genderOptions"
                        label="Género"
                        :rules="[rules.required]"
                        variant="outlined"
                        density="comfortable"
                        class="mb-2"
                    />
                    <v-text-field
                        v-model="form.origin"
                        label="Origen"
                        :rules="[rules.required]"
                        variant="outlined"
                        density="comfortable"
                        class="mb-2"
                    />
                    <v-text-field
                        v-model="form.destination"
                        label="Destino"
                        :rules="[rules.required]"
                        variant="outlined"
                        density="comfortable"
                        class="mb-2"
                    />
                    <v-select
                        v-model="form.reason"
                        :items="reasonOptions"
                        label="Motivo del viaje"
                        :rules="[rules.required]"
                        variant="outlined"
                        density="comfortable"
                        class="mb-2"
                    />
                    <v-text-field
                        v-model.number="form.occupants"
                        label="Cantidad de ocupantes"
                        type="number"
                        min="0"
                        step="1"
                        :rules="[rules.required, rules.nonNegative, rules.integer]"
                        variant="outlined"
                        density="comfortable"
                        class="mb-2"
                    />
                </v-form>
            </v-card-text>
            <v-card-actions class="px-4 pb-4">
                <v-spacer />
                <v-btn
                    color="primary"
                    variant="elevated"
                    :loading="saving"
                    @click="submitForm"
                    prepend-icon="mdi-content-save"
                >
                    Guardar encuesta
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-container>
</template>

<script lang="ts" setup>
import { ref, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useSurveyResponseStore } from '@/stores/SurveyResponseStore';
import { createEmptySurvey, SurveyData } from '@/services/survey-response/models/surveyModels';
import { useToast } from 'vue-toast-notification';

const router = useRouter();
const route = useRoute();
const store = useSurveyResponseStore();
const toast = useToast();

const formRef = ref();
const saving = ref(false);

const genderOptions = [
    { title: 'Masculino', value: 'Masculino' },
    { title: 'Femenino', value: 'Femenino' },
];
const reasonOptions = [
    { title: 'Trabajo', value: 'trabajar' },
    { title: 'Estudio', value: 'estudiar' },
    { title: 'Compras', value: 'compras' },
    { title: 'Recreación', value: 'recreacion' },
    { title: 'Volver a casa', value: 'volver_a_casa' },
    { title: 'Otro', value: 'otro' },
];

const rules = {
    required: (v: any) => !!v || v === 0 || 'Campo requerido',
    nonNegative: (v: number) => (v !== undefined && v >= 0) || 'No puede ser negativo',
    integer: (v: number) => Number.isInteger(v) || 'Debe ser un número entero',
};

const form = ref<SurveyData>(createEmptySurvey());

function normalizeGender(gender: string): string {
    const legacyValues: Record<string, string> = {
        masc: 'Masculino',
        fem: 'Femenino',
    };
    return legacyValues[gender.toLowerCase()] || gender;
}

onMounted(async () => {
    const id = route.params.id as string;
    if (id && id !== 'new') {
        // Editing existing survey — data loaded by store via App.vue watcher
        if (store.survey) {
            form.value = { ...store.survey, gender: normalizeGender(store.survey.gender) };
        }
    }
});

// Watch for store survey changes (e.g., after fetch completes)
import { watch } from 'vue';
watch(() => store.survey, (newSurvey) => {
    if (newSurvey && route.params.id !== 'new') {
        form.value = { ...newSurvey, gender: normalizeGender(newSurvey.gender) };
    }
});

async function submitForm() {
    const { valid } = await formRef.value.validate();
    if (!valid) return;

    saving.value = true;
    try {
        const success = await store.saveSurveyResponse(form.value);
        if (success) {
            toast.success('Encuesta guardada correctamente');
            router.push({ name: 'saved-survey' });
        } else {
            toast.error('Error al guardar la encuesta');
        }
    } catch (error) {
        console.error('Error saving survey:', error);
        toast.error('Error al guardar la encuesta');
    } finally {
        saving.value = false;
    }
}
</script>
